import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardHeader, CardContent } from '../../components/ui';
import { Bell, Check, Loader2 } from 'lucide-react';
import { Notification as NotificationType } from '@court-sync/shared';
import { notificationService } from '../../services/notification';
import { PageHeader } from '@/components/layout/PageHeader';

type FilterType = 'all' | 'unread' | 'booking' | 'payment' | 'lesson' | 'system';

export default function Notifications() {
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Load notifications on mount and when page changes
  useEffect(() => {
    loadNotifications();
  }, [page]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getNotifications(page, 20);
      if (response.success && response.data) {
        setNotifications(response.data.notifications || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Map notification type to display category
  const getNotificationCategory = (type: string): 'booking' | 'payment' | 'lesson' | 'system' => {
    if (type.includes('booking')) return 'booking';
    if (type.includes('play_event')) return 'booking';
    if (type.includes('makeup_credit')) return 'booking';
    if (type.includes('accounting')) return 'payment';
    if (type.includes('payment')) return 'payment';
    if (type.includes('lesson')) return 'lesson';
    return 'system';
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.isRead;
    const category = getNotificationCategory(notification.type);
    return category === filter;
  });

  const markAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, isRead: true }
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      );
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const formatTimeAgo = (date: Date | string | any): string => {
    const now = new Date();
    let notificationDate: Date;
    
    // Handle Firestore Timestamp objects
    if (date && typeof date === 'object' && 'toDate' in date) {
      notificationDate = date.toDate();
    } else if (typeof date === 'string') {
      notificationDate = new Date(date);
    } else if (date instanceof Date) {
      notificationDate = date;
    } else {
      return 'Just now';
    }
    
    const diffInSeconds = Math.floor((now.getTime() - notificationDate.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds} sec ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} min ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
      return notificationDate.toLocaleDateString();
    }
  };

  const getTypeColor = (category: 'booking' | 'payment' | 'lesson' | 'system') => {
    switch (category) {
      case 'booking': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'payment': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'lesson': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'system': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getTypeIcon = (category: 'booking' | 'payment' | 'lesson' | 'system') => {
    switch (category) {
      case 'booking': return '📅';
      case 'payment': return '💳';
      case 'lesson': return '🎾';
      case 'system': return '⚙️';
      default: return '📢';
    }
  };

  return (
    <div className="contents">
        <PageHeader
          title="Notifications"
          subtitle="View and manage your in-app notifications"
        />
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Filters
                </h3>
              </CardHeader>
              <CardContent className="space-y-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'all'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  All Notifications ({notifications.length})
                </button>
                <button
                  onClick={() => setFilter('unread')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'unread'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  Unread ({notifications.filter(n => !n.isRead).length})
                </button>
                <button
                  onClick={() => setFilter('booking')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'booking'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  📅 Bookings ({notifications.filter(n => getNotificationCategory(n.type) === 'booking').length})
                </button>
                <button
                  onClick={() => setFilter('payment')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'payment'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  💳 Payments ({notifications.filter(n => getNotificationCategory(n.type) === 'payment').length})
                </button>
                <button
                  onClick={() => setFilter('lesson')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'lesson'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  🎾 Lessons ({notifications.filter(n => getNotificationCategory(n.type) === 'lesson').length})
                </button>
                <button
                  onClick={() => setFilter('system')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === 'system'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  ⚙️ System ({notifications.filter(n => getNotificationCategory(n.type) === 'system').length})
                </button>
              </CardContent>
            </Card>
          </div>

          {/* Notifications List */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {filter === 'all' ? 'All Notifications' : 
                       filter === 'unread' ? 'Unread Notifications' :
                       filter.charAt(0).toUpperCase() + filter.slice(1) + ' Notifications'}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  {notifications.filter(n => !n.isRead).length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={markAllAsRead}
                      className="flex items-center space-x-2"
                    >
                      <Check className="h-4 w-4" />
                      <span>Mark all as read</span>
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">Loading notifications...</p>
                  </div>
                ) : filteredNotifications.length === 0 ? (
                  <div className="text-center py-12">
                    <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No notifications
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {filter === 'unread' 
                        ? "You're all caught up! No unread notifications."
                        : `No ${filter} notifications found.`
                      }
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4">
                      {filteredNotifications.map((notification) => {
                        const category = getNotificationCategory(notification.type);
                        return (
                          <div
                            key={notification.id}
                            className={`p-4 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors ${
                              !notification.isRead 
                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                            }`}
                          >
                            <div className="flex items-start space-x-4">
                              <div className="flex-shrink-0">
                                <span className="text-2xl">{getTypeIcon(category)}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  <h3 className={`text-sm font-medium ${
                                    !notification.isRead 
                                      ? 'text-gray-900 dark:text-white' 
                                      : 'text-gray-700 dark:text-gray-300'
                                  }`}>
                                    {notification.title}
                                  </h3>
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(category)}`}>
                                    {category}
                                  </span>
                                  {!notification.isRead && (
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                  {notification.body}
                                </p>
                                {notification.type === 'lesson.cancelled' &&
                                  (notification.data as { creditsUrl?: string })?.creditsUrl && (
                                  <Link
                                    to={(notification.data as { creditsUrl?: string }).creditsUrl!.replace(
                                      /^https?:\/\/[^/]+/,
                                      ''
                                    )}
                                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                                  >
                                    View make-up credits →
                                  </Link>
                                )}
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                  {formatTimeAgo(notification.createdAt)}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                {!notification.isRead && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => markAsRead(notification.id)}
                                    className="p-1"
                                    title="Mark as read"
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(prev => Math.max(1, prev - 1))}
                          disabled={page === 1}
                        >
                          Previous
                        </Button>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Page {page} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={page === totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
  );
}
